import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Users, Mail, Phone, Calendar, TrendingUp, Clock, LogOut, Megaphone, MapPin, Camera, Filter, X, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'new', label: 'Nouveau', bg: 'bg-blue-100', text: 'text-blue-800' },
  { value: 'contacted', label: 'Contacté', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  { value: 'in_progress', label: 'En cours', bg: 'bg-orange-100', text: 'text-orange-800' },
  { value: 'done', label: 'Terminé', bg: 'bg-green-100', text: 'text-green-800' },
  { value: 'lost', label: 'Perdu', bg: 'bg-gray-100', text: 'text-gray-600' },
];

const getStatusStyle = (status) => STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
};

const calcStats = (data) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(today.getTime() - 7 * 86400000);
  const monthAgo = new Date(today.getTime() - 30 * 86400000);
  return {
    total: data.length,
    today: data.filter((c) => new Date(c.created_at) >= today).length,
    thisWeek: data.filter((c) => new Date(c.created_at) >= weekAgo).length,
    thisMonth: data.filter((c) => new Date(c.created_at) >= monthAgo).length,
  };
};

const StatCard = ({ label, value, color, icon: Icon }) => (
  <Card className={`border-2 border-${color}-200 bg-gradient-to-br from-${color}-50 to-white`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">{label}</p>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
        </div>
        <div className={`bg-${color}-100 p-2.5 rounded-full`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

/* ─── Photo Lightbox ─── */
function PhotoLightbox({ photos, onClose }) {
  const [idx, setIdx] = useState(0);
  const prev = () => setIdx((i) => (i > 0 ? i - 1 : photos.length - 1));
  const next = () => setIdx((i) => (i < photos.length - 1 ? i + 1 : 0));

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowLeft') prev(); if (e.key === 'ArrowRight') next(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center" onClick={onClose} data-testid="photo-lightbox">
      <div className="relative max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white hover:text-gray-300" data-testid="lightbox-close">
          <X size={24} />
        </button>
        <img
          src={`${BACKEND_URL}/api/files/${photos[idx]}`}
          alt={`Photo ${idx + 1}`}
          className="w-full max-h-[70vh] object-contain rounded-lg"
          data-testid="lightbox-image"
        />
        {photos.length > 1 && (
          <>
            <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2">
              <ChevronLeft size={20} />
            </button>
            <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2">
              <ChevronRight size={20} />
            </button>
          </>
        )}
        <p className="text-center text-white text-sm mt-3">{idx + 1} / {photos.length}</p>
      </div>
    </div>
  );
}

/* ─── Status Dropdown ─── */
function StatusSelect({ leadId, currentStatus, onUpdate }) {
  const [updating, setUpdating] = useState(false);
  const style = getStatusStyle(currentStatus);

  const handleChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === currentStatus) return;
    setUpdating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) onUpdate(leadId, newStatus);
    } catch (err) { console.error(err); }
    finally { setUpdating(false); }
  };

  return (
    <select
      value={currentStatus || 'new'}
      onChange={handleChange}
      disabled={updating}
      data-testid={`status-select-${leadId}`}
      className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${style.bg} ${style.text} ${updating ? 'opacity-50' : ''}`}
    >
      {STATUS_OPTIONS.map((s) => (
        <option key={s.value} value={s.value}>{s.label}</option>
      ))}
    </select>
  );
}

/* ─── Main Admin ─── */
const Admin = () => {
  const [tab, setTab] = useState('leads');
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterVille, setFilterVille] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [lightboxPhotos, setLightboxPhotos] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem('adminAuth')) { navigate('/admin/login'); return; }
    fetchAll();
  }, [navigate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cRes, lRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/contact`),
        fetch(`${BACKEND_URL}/api/leads`),
      ]);
      const cData = cRes.ok ? await cRes.json() : [];
      const lData = lRes.ok ? await lRes.json() : [];
      setContacts(cData);
      setLeads(lData);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleStatusUpdate = useCallback((leadId, newStatus) => {
    setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, status: newStatus } : l));
  }, []);

  const contactStats = calcStats(contacts);
  const leadStats = calcStats(leads);
  const uniqueVilles = [...new Set(leads.map((l) => l.ville).filter(Boolean))].sort();
  const uniqueSources = [...new Set(leads.map((l) => l.source).filter(Boolean))].sort();

  const filteredLeads = leads.filter((l) => {
    if (filterVille && l.ville !== filterVille) return false;
    if (filterSource && l.source !== filterSource) return false;
    return true;
  });

  const sourceLabel = (s) => {
    if (!s) return '-';
    return s.replace('landing-google-ads-', '').replace('landing-page', 'Direct');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {lightboxPhotos && <PhotoLightbox photos={lightboxPhotos} onClose={() => setLightboxPhotos(null)} />}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-title">Tableau de bord Admin</h1>
            <p className="text-gray-500 text-sm">Les Façadiers HDF — Gestion des demandes</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="admin-tabs">
          <button onClick={() => setTab('leads')} data-testid="tab-leads"
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'leads' ? 'bg-cyan-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            <Megaphone className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Leads Google Ads
            <span className="ml-1.5 bg-white/20 text-xs px-1.5 py-0.5 rounded-full">{leads.length}</span>
          </button>
          <button onClick={() => setTab('contacts')} data-testid="tab-contacts"
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'contacts' ? 'bg-cyan-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            <Mail className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Contacts site
            <span className="ml-1.5 bg-white/20 text-xs px-1.5 py-0.5 rounded-full">{contacts.length}</span>
          </button>
        </div>

        {/* ─── LEADS TAB ─── */}
        {tab === 'leads' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard label="Total leads" value={leadStats.total} color="cyan" icon={Megaphone} />
              <StatCard label="Aujourd'hui" value={leadStats.today} color="green" icon={Calendar} />
              <StatCard label="Cette semaine" value={leadStats.thisWeek} color="blue" icon={TrendingUp} />
              <StatCard label="Ce mois" value={leadStats.thisMonth} color="purple" icon={Clock} />
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="lead-filters">
              <Filter className="w-4 h-4 text-gray-400" />
              <select value={filterVille} onChange={(e) => setFilterVille(e.target.value)} data-testid="filter-ville"
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-cyan-500">
                <option value="">Toutes les villes</option>
                {uniqueVilles.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} data-testid="filter-source"
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-cyan-500">
                <option value="">Toutes les sources</option>
                {uniqueSources.map((s) => <option key={s} value={s}>{sourceLabel(s)}</option>)}
              </select>
              {(filterVille || filterSource) && (
                <button onClick={() => { setFilterVille(''); setFilterSource(''); }}
                  className="text-xs text-cyan-600 hover:underline">Effacer les filtres</button>
              )}
              <span className="text-xs text-gray-400 ml-auto">{filteredLeads.length} résultat(s)</span>
            </div>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between py-4">
                <CardTitle className="text-lg">Leads Google Ads</CardTitle>
                <Button onClick={fetchAll} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="refresh-btn">Actualiser</Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-10">
                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-600" />
                  </div>
                ) : filteredLeads.length === 0 ? (
                  <div className="text-center py-10">
                    <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Aucun lead pour le moment</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Nom</TableHead>
                          <TableHead>Téléphone</TableHead>
                          <TableHead>Ville</TableHead>
                          <TableHead>Travaux</TableHead>
                          <TableHead>Message</TableHead>
                          <TableHead>Photos</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Statut</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredLeads.map((lead) => (
                          <TableRow key={lead.id} className="hover:bg-gray-50" data-testid={`lead-row-${lead.id}`}>
                            <TableCell className="text-xs text-gray-500 whitespace-nowrap">{formatDate(lead.created_at)}</TableCell>
                            <TableCell className="font-medium text-sm">{lead.name}</TableCell>
                            <TableCell>
                              <a href={`tel:${lead.phone}`} className="text-cyan-600 hover:underline flex items-center gap-1 text-sm">
                                <Phone className="w-3.5 h-3.5" /> {lead.phone}
                              </a>
                            </TableCell>
                            <TableCell>
                              {lead.ville ? <span className="flex items-center gap-1 text-sm"><MapPin className="w-3.5 h-3.5 text-cyan-500" />{lead.ville}</span> : '-'}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">{lead.type_travaux || '-'}</TableCell>
                            <TableCell className="max-w-[200px]">
                              <div className="truncate text-sm text-gray-500" title={lead.message}>{lead.message || '-'}</div>
                            </TableCell>
                            <TableCell>
                              {lead.photo_urls?.length > 0 ? (
                                <button
                                  onClick={() => setLightboxPhotos(lead.photo_urls)}
                                  data-testid={`photos-btn-${lead.id}`}
                                  className="flex items-center gap-1 text-xs text-cyan-600 hover:text-cyan-800 hover:underline"
                                >
                                  <Camera className="w-3.5 h-3.5" /> {lead.photo_urls.length} photo(s)
                                </button>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-50 text-cyan-700">
                                {sourceLabel(lead.source)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <StatusSelect leadId={lead.id} currentStatus={lead.status || 'new'} onUpdate={handleStatusUpdate} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* ─── CONTACTS TAB ─── */}
        {tab === 'contacts' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard label="Total contacts" value={contactStats.total} color="cyan" icon={Users} />
              <StatCard label="Aujourd'hui" value={contactStats.today} color="green" icon={Calendar} />
              <StatCard label="Cette semaine" value={contactStats.thisWeek} color="blue" icon={TrendingUp} />
              <StatCard label="Ce mois" value={contactStats.thisMonth} color="purple" icon={Clock} />
            </div>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between py-4">
                <CardTitle className="text-lg">Demandes de contact</CardTitle>
                <Button onClick={fetchAll} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white">Actualiser</Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-10">
                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-600" />
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="text-center py-10">
                    <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Aucune demande de contact</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Nom</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Téléphone</TableHead>
                          <TableHead>Adresse</TableHead>
                          <TableHead>Message</TableHead>
                          <TableHead>Statut</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contacts.map((c) => (
                          <TableRow key={c.id} className="hover:bg-gray-50">
                            <TableCell className="text-xs text-gray-500 whitespace-nowrap">{formatDate(c.created_at)}</TableCell>
                            <TableCell className="font-medium text-sm">{c.name}</TableCell>
                            <TableCell>
                              <a href={`mailto:${c.email}`} className="text-cyan-600 hover:underline flex items-center gap-1 text-sm">
                                <Mail className="w-3.5 h-3.5" /> {c.email}
                              </a>
                            </TableCell>
                            <TableCell>
                              <a href={`tel:${c.phone}`} className="text-cyan-600 hover:underline flex items-center gap-1 text-sm">
                                <Phone className="w-3.5 h-3.5" /> {c.phone}
                              </a>
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">{c.address || '-'}</TableCell>
                            <TableCell className="max-w-[200px]">
                              <div className="truncate text-sm text-gray-500" title={c.message}>{c.message}</div>
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {c.status === 'new' ? 'Nouveau' : c.status}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        <div className="mt-6 text-center text-xs text-gray-400">
          Les données sont actualisées en temps réel — Toutes les demandes sont sauvegardées en base de données
        </div>
      </div>
    </div>
  );
};

export default Admin;
